import type { VueFinalModalProperty } from 'vue-final-modal';

import { getVm } from '@/helpers/vm-helper';
import { getLastProView } from './pro-web-helper';
import { TrackingHelper } from './tracking/tracking-helper';
import type { SnowplowContext, TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';

function getVfm() {
	return getVm().$vfm as VueFinalModalProperty | null;
}

async function openModal(
	component: any,
	componentProps: any = {},
	options: any = {},
	location = '', // For tracking.
	customModalOpenEvent = false,
	additionalContexts: SnowplowContext[] = [],
	onCloseCallback?: Function,
	init?: Function
) {
	init?.();

	const $vfm = getVfm();
	if (!$vfm) {
		throw new Error('VFM not available');
	}

	const modalId = `modal-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
	let resolve: (value: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	let reject: (reason?: any) => void;

	const modalPromise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});

	const { id, cssClass, ...restOptions } = options;
	const modalProps = {
		parent: getVm(),
		...(id && { id }),
		...(cssClass && { class: cssClass }),
		onWillDismiss: () => {},
		onDidDismiss: () => {},
		...componentProps,
		location,
	};

	let componentName = component?.extendOptions?.name || component?.__name;
	if (!componentName) {
		const dynamicComponent = await component();
		componentName = dynamicComponent?.default?.extendOptions?.name || dynamicComponent?.default.name;
	}

	const trackingInfo: TrackingProviderPropertiesInterface = { action: 'modal_opened' };

	switch (componentName) {
		case 'AccountModal':
			if (location !== '') trackingInfo.property = location;
		case 'SettingsModal':
			trackingInfo.label = 'user_account';
			break;
		case 'MarkAsSeenModal':
			trackingInfo.label = 'tv_show_tracking';
			break;
		case 'MarkAsSeenModalGraphql':
			trackingInfo.label = 'tv_show_tracking';
			break;
		case 'CancelProModal':
			if (location !== '') trackingInfo.property = location;
			trackingInfo.label = 'FreemiumCancellationModal';
			break;
		case 'SearchTitlesToAddModal':
			trackingInfo.label = 'SearchTitlesToAddLists';
			trackingInfo.property = 'lists';
			break;
		case 'AddToListModal':
		case 'CreateCustomListModal':
		case 'RenameListModal':
			trackingInfo.label = componentName;
			trackingInfo.property = 'lists';
			break;
		case 'ConsentModal':
			trackingInfo.nonInteraction = true;
		case 'JwtvTitleRentalModal':
			trackingInfo.property = 'jwtv-rent-modal';
			break;
		default:
			trackingInfo.label = componentName;
			break;
	}

	if (!customModalOpenEvent) {
		TrackingHelper.trackEvent('userinteraction', trackingInfo, additionalContexts);
	}

	$vfm.show({
		component: 'VueFinalModal',
		bind: {
			name: modalId,
			escToClose: true,
		},
		on: {
			'before-close': () => {
				modalProps?.onWillDismiss();
			},
			closed: () => {
				modalProps?.onDidDismiss();

				onCloseCallback?.();

				if (!customModalOpenEvent) {
					TrackingHelper.trackEvent(
						'userinteraction',
						{
							action: 'modal_closed',
							label: trackingInfo.label,
							property:
								componentName === 'ProModal'
									? getLastProView() || undefined
									: componentName === 'CancelProModal'
									? 'SettingsProSubscription'
									: trackingInfo.property,
							nonInteraction: componentName === 'ConsentModal' ? true : undefined,
						},
						additionalContexts
					);
				}
			},
		},
		slots: {
			default: {
				component,
				bind: modalProps,
				on: {
					dismiss: (data?: any) => {
						if (resolve) {
							$vfm.hide(modalId);
							resolve(data || null);
						}
					},
				},
			},
		},
		...restOptions,
	});

	return modalPromise;
}

async function close(id?: string) {
	const $vfm = getVfm();
	if (!$vfm) return;

	try {
		if (id) {
			$vfm.hide(id);
		} else {
			$vfm.hideAll();
		}
	} catch (error) {
		console.error('Modal close error', error);
	}
}

export const ModalHelper = {
	openModal,
	close,
};
